import React, {
    useEffect, useMemo, useRef, useState
} from "react";
import {
    Box,
    CircularProgress,
    Grid,
    Typography,
    Container,
    TableCell,
    TableRow,
    tableCellClasses,
    Tooltip,
} from "@mui/material";
import styled from "@emotion/styled";
import { useParams } from "react-router-dom";
import Colors from "assets/Style/Colors";
import { ErrorToaster } from "components/Toaster";
import { PrimaryButton } from "components/Buttons";
import {
    CommaSeparator,
    handleExportWithComponent
} from "utils";
import {
    FontFamily,
    //   Images,
    InvoiceGlobal,
    InvoiceLocation,
    InvoiceMail,
    InvoicePhone,
} from "assets";
import { makeStyles } from "@mui/styles";
import { QRCodeCanvas } from "qrcode.react";
import { useReactToPrint } from "react-to-print";
import { PDFExport } from "@progress/kendo-react-pdf";
import LogoImage from "assets/Images/header_voucher.svg";
import moment from "moment";
import { Print } from "@mui/icons-material";
import SupplierServices from "services/Supplier";

// *For Table Style
const Row = styled(TableRow)(({ theme }) => ({
    border: 0,
}));

const Cell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        fontSize: 14,
        fontFamily: FontFamily.openSans,
        border: 0,
        textAlign: "center",
        whiteSpace: "wrap",
        background: Colors.primary,
        color: Colors.white,
        overFlowWrap: "wrap"
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        fontFamily: FontFamily.openSans,
        textAlign: "center",
        textWrap: "wrap !important",
        padding: "5px !important",

        ".MuiBox-root": {
            display: "flex",
            gap: "6px",
            alignItems: "center",
            justifyContent: "center",
            ".MuiBox-root": {
                cursor: "pointer",
            },
        },
        svg: {
            width: "auto",
            height: "24px",
        },
        ".MuiTypography-root": {
            textTransform: "capitalize",
            fontFamily: FontFamily.openSans,
            textWrap: "nowrap",
        },
        ".MuiButtonBase-root": {
            padding: "8px",
            width: "28px",
            height: "28px",
        },
    },
}));

const useStyle = makeStyles({
    headingBg: {
        margin: "32px 0px",
        padding: "12px 0px",
        textAlign: "center",
    },
    heading: {
        color: Colors.white,
        textTransform: "uppercase",
        fontWeight: 300,
        fontFamily: FontFamily.openSans,
    },
    text: {
        color: Colors.smokeyGrey,
        fontWeight: 300,
        fontFamily: FontFamily.openSans,
    },
    tableCell: {
        backgroundColor: Colors.aliceBlue,
        border: "0.25px solid #D9D9D9",
        "& .MuiTypography-root": {
            padding: "4px 12px",
        },
    },
});

function SupplierBillApprovalReceipt() {
    const { id } = useParams();
    const contentRef = useRef(null);
    const classes = useStyle();

    const tableHead = [
        "Sr.No",
        "Date",
        "Bill no.",
        "Ref no",
        "Total Amount",
        "Paid Amount",
        "Balance",
    ];
    const USD_TO_AED = 3.6725;
    const convertAedToUsd = (amount) => Number(amount || 0) / USD_TO_AED;
    const [loader, setLoader] = useState(true);
    console.log(loader, 'loader')
    // *For Journal Voucher Detail
    const [voucherDetail, setVoucherDetail] = useState();
    const [isContentReady, setIsContentReady] = useState(false);
    const [billDetails, setBillDetails] = useState([]);
    console.log(billDetails, 'billDetails')
    const invoiceDetail = []


    const hasAccountDetail = voucherDetail?.new_voucher;

    const handlePrint = useReactToPrint({
        content: () => contentRef.current,
        documentTitle: "Invoice",
        onBeforePrint: () => {
            const content = contentRef.current;
            if (content && isContentReady) {
                content.style.transform = 'scale(0.83)';
                content.style.transformOrigin = 'top center';
            } else {
                console.warn('contentRef.current is not available in onBeforePrint');
            }
        },
        onAfterPrint: () => {
            try {
                const content = contentRef.current;
                if (content) {

                    content.style.transform = 'none';
                } else {
                    console.warn('contentRef.current is null or undefined in onAfterPrint');
                }
            } catch (error) {
                console.error('Error in onAfterPrint:', error);
            }
        },
    });

    const getLastPathSegment = (path) => {
        if (!path) return "-";
        const segments = path.split("→");
        return segments[segments.length - 1]?.trim();
    };

    const getBillDataDetails = async () => {
        setLoader(true);

        try {

            let params = {
                bill_id: id,
            }

            const data = await SupplierServices.getBillDetails(params);
            setBillDetails(data?.data?.bill)
        } catch (error) {
            ErrorToaster(error);
        } finally {
            setLoader(false)
        }

    };



    useEffect(() => {
        getBillDataDetails();
    }, [])

    const totalAmount = useMemo(() => {
        if (!billDetails?.bill_accounts) return 0;
        return billDetails.bill_accounts.reduce((sum, account) => {
            return sum + (Number(account.total_amount) || 0);
        }, 0);
    }, [billDetails]);


    const paidAmount = useMemo(() => {
        if (!billDetails?.bill_accounts) return parseFloat(billDetails?.paid_amount || 0);
        const sum = billDetails.bill_accounts.reduce((acc, account) => {
            return acc + (Number(account.paid_amount) || 0);
        }, 0);
        return sum > 0 ? sum : parseFloat(billDetails?.paid_amount || 0);
    }, [billDetails]);

    const balance = totalAmount - paidAmount;


    const totalVAT = useMemo(() => {
        if (!billDetails?.bill_accounts) return 0;
        return billDetails.bill_accounts.reduce((sum, account) => {
            return sum + (Number(account.vat_amount) || 0);
        }, 0);
    }, [billDetails]);


    const totalBillAmount = useMemo(() => {
        if (!billDetails?.bill_accounts) return 0;
        return billDetails.bill_accounts.reduce((sum, account) => {
            return sum + (Number(account.bill_amount) || 0);
        }, 0);
    }, [billDetails]);


    const pmtStatus = billDetails?.pmt_status?.toString().toLowerCase();
    const isPaid = pmtStatus === "paid" || pmtStatus === "partial";
    const receiptTitle = isPaid ? "Supplier Bill Payment" : "Supplier Bill Booked";
    const getUniqueTextValues = (values) =>
        values
            .map((value) => value?.toString().trim())
            .filter(Boolean)
            .filter((value, index, array) => array.indexOf(value) === index);
    const formatProcessedOn = (value) => {
        if (!value) return "-";
        return moment(value).format(isPaid ? "DD-MM-YYYY hh:mm A" : "DD-MM-YYYY");
    };
    const formatPaymentHistoryDate = (payment) => {
        const selectedPaidDate = payment?.pay_date || payment?.pay_bill_date;

        if (selectedPaidDate) {
            return moment(selectedPaidDate).format("DD-MM-YYYY");
        }

        if (payment?.created_at) {
            return moment(payment.created_at).format("DD-MM-YYYY hh:mm A");
        }

        return "-";
    };
    const groupedPaymentHistory = useMemo(() => {
        const grouped = {};

        (billDetails?.payment_history ?? []).forEach((payment) => {
            const selectedPaidDate = payment?.pay_date || payment?.pay_bill_date || "";
            const processedAt = payment?.created_at || "";
            const paymentMethod = payment?.payment_method || "";
            const paymentNotes = getUniqueTextValues([payment?.remarks, payment?.notes]).join(" | ");
            const key = [selectedPaidDate, processedAt, paymentMethod, paymentNotes].join("__");

            if (!grouped[key]) {
                grouped[key] = {
                    ...payment,
                    paid_amount: 0,
                };
            }

            grouped[key].paid_amount += parseFloat(payment?.paid_amount || 0);
        });

        return Object.values(grouped);
    }, [billDetails]);
    const processedOnValue = useMemo(() => {
        if (!isPaid) {
            return billDetails?.created_at;
        }

        const paymentHistory = billDetails?.payment_history ?? [];
        if (paymentHistory.length === 0) {
            return billDetails?.created_at;
        }

        const latestPayment = [...paymentHistory].sort((left, right) => {
            const leftValue = left?.created_at || left?.pay_date || left?.pay_bill_date || "";
            const rightValue = right?.created_at || right?.pay_date || right?.pay_bill_date || "";
            return moment(rightValue).valueOf() - moment(leftValue).valueOf();
        })[0];

        return latestPayment?.created_at || latestPayment?.pay_date || latestPayment?.pay_bill_date || billDetails?.created_at;
    }, [billDetails, isPaid]);
    const receiptRemarks = useMemo(() => {
        const paymentRemarks = getUniqueTextValues(
            (billDetails?.payment_history ?? []).flatMap((item) => [item?.remarks, item?.notes]),
        );

        if (isPaid && paymentRemarks.length > 0) {
            return paymentRemarks.join(", ");
        }

        const accountRemarks = getUniqueTextValues(
            (billDetails?.bill_accounts ?? []).flatMap((item) => [item?.remarks, item?.notes]),
        );

        if (accountRemarks.length > 0) {
            return accountRemarks.join(", ");
        }

        return getUniqueTextValues([billDetails?.remarks, billDetails?.notes]).join(", ") || "-";
    }, [billDetails, isPaid]);

    return (
        <Container>
            {!loader && (
                <Box sx={{ textAlign: "right", p: 4 }}>
                    <PrimaryButton
                        title="Download PDF"
                        type="button"
                        style={{ backgroundColor: Colors.bluishCyan }}
                        onClick={() => handleExportWithComponent(contentRef)}
                    />
                    <Box
                        component="div"
                        sx={{
                            display: "inline-block",
                            marginLeft: 2,
                            cursor: "pointer",
                        }}
                        onClick={() => {

                            handlePrint()
                        }}
                    >
                        <Print sx={{ height: 30, width: 30, cursor: "pointer", color: Colors.primary }} />
                    </Box>
                    {/* <Box
                        component="div"
                        sx={{ display: "inline-block", marginLeft: 2 }}
                    >
                        <PrimaryButton
                            title="Open Preview"
                            type="button"
                            onClick={() => window.open(`/supplier-bill-preview/${btoa(String(id))}`, '_blank')}
                        />
                    </Box> */}
                </Box>
            )}
            <PDFExport ref={contentRef} fileName={receiptTitle}>
                <Box
                    sx={{
                        width: "1000px",
                        mx: 4,
                        my: 2,
                        bgcolor: Colors.white,
                        boxShadow: "0px 8px 18px 0px #9B9B9B1A",
                    }}
                >

                    <>

                        {!loader ? (
                            <Box>
                                <Grid container spacing={0}>
                                    <Box
                                        sx={{
                                            width: { xs: "100%", md: "25%" },
                                            "@media print": {
                                                width: "25%",
                                                flex: "0 0 25%",
                                            },
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={LogoImage}
                                            alt="Ship Logo"
                                            sx={{
                                                width: "100%",
                                                objectFit: "contain",
                                                display: "block",
                                                maxHeight: "120px",
                                            }}
                                        />
                                    </Box>

                                    {/* Right Contact Info */}
                                    <Box
                                        sx={{
                                            // bgcolor: Colors.primary,
                                            backgroundColor: "#0C6135 ",
                                            padding: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            width: { xs: "100%", md: "75%" },
                                            "@media print": {
                                                width: "75%",
                                                flex: "0 0 75%",
                                            },
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 1,
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <InvoicePhone />
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            color: Colors.whiteSmoke,
                                                            fontFamily: FontFamily.openSans,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        +971 6 510 2000
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 1,
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <InvoiceMail />
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            color: Colors.whiteSmoke,
                                                            fontFamily: FontFamily.openSans,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        info@galaxyshipping.com
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 1,
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <InvoiceGlobal />
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            color: Colors.whiteSmoke,
                                                            fontFamily: FontFamily.openSans,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        https://galaxyshipping.com
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 1,
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <InvoiceLocation />
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            color: Colors.whiteSmoke,
                                                            fontFamily: FontFamily.openSans,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        Ind Area#4 P.O Box 83126, Sharjah, UAE
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                                <Box
                                    sx={{
                                        mt: 5,
                                        mb: 0.5,
                                        position: "relative",
                                        // backgroundColor: '#0C6135 ',
                                        bgcolor: Colors.primary,
                                        width: 1,
                                        height: "12px",
                                    }}
                                >
                                    <Typography
                                        component={"span"}
                                        variant="h2"
                                        sx={{
                                            fontFamily: "Helvetica",
                                            color: Colors.charcoalGrey,
                                            bgcolor: Colors.white,
                                            pl: 2,
                                            pr: 2,
                                            pt: 2,
                                            letterSpacing: "3px",
                                            position: "absolute",
                                            right: "90px",
                                            top: "-40px",
                                        }}
                                    >
                                        {receiptTitle}
                                    </Typography>
                                </Box>

                                <Grid container>
                                    <Grid item xs={6} sx={{ px: 2, py: 0.5 }}>
                                        <Grid container sx={{ padding: "10px" }}>
                                            <Grid item xs={5}>
                                                <Typography variant="body1">Supplier Code:</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body1" sx={{ fontFamily: FontFamily.NunitoRegular }}>
                                                    {billDetails?.suppliers?.id ? `GLS-${String(billDetails.suppliers.id).padStart(4, '0')}` : '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={5}>
                                                <Typography variant="body1">Supplier Name:</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body1" sx={{ fontFamily: FontFamily.NunitoRegular }}>
                                                    {billDetails?.suppliers?.company_name || billDetails?.suppliers?.authorized_person || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={5}>
                                                <Typography variant="body1">Email Id:</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body1" sx={{ fontFamily: FontFamily.NunitoRegular }}>
                                                    {billDetails?.suppliers?.email ?? '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={5}>
                                                <Typography variant="body1">Phone No:</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body1" sx={{ fontFamily: FontFamily.NunitoRegular }}>
                                                    {billDetails?.suppliers?.phone ?? '-'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={6} sx={{ px: 2, py: 1 }}>
                                        <Grid container sx={{ padding: "10px" }}>
                                            <Grid item xs={5}>
                                                <Typography variant="body1">Bill No.:</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body1" sx={{ fontFamily: FontFamily.NunitoRegular }}>
                                                    {billDetails?.bill_no || '-'}
                                                </Typography>
                                            </Grid>
                                            {isPaid && (
                                                <>
                                                    <Grid item xs={5}>
                                                        <Typography variant="body1">Ext. Ref. No.:</Typography>
                                                    </Grid>
                                                    <Grid item xs={7}>
                                                        <Typography variant="body1" sx={{ fontFamily: FontFamily.NunitoRegular }}>
                                                            {billDetails?.external_ref_no || '-'}
                                                        </Typography>
                                                    </Grid>
                                                </>
                                            )}
                                            {isPaid && (
                                                <>
                                                    <Grid item xs={5}>
                                                        <Typography variant="body1">Pmt. Ref. #:</Typography>
                                                    </Grid>
                                                    <Grid item xs={7}>
                                                        <Typography variant="body1" sx={{ fontFamily: FontFamily.NunitoRegular }}>
                                                            {billDetails?.ref_no ? `REF-${billDetails.ref_no}` : '-'}
                                                        </Typography>
                                                    </Grid>
                                                </>
                                            )}
                                            <Grid item xs={5}>
                                                <Typography variant="body1">Processed on:</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body1" sx={{ fontFamily: FontFamily.NunitoRegular }}>
                                                    {formatProcessedOn(processedOnValue)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={5}>
                                                <Typography variant="body1"> Bill Date:</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body1" sx={{ fontFamily: FontFamily.NunitoRegular }}>
                                                    {moment(billDetails?.bill_date).format("DD-MM-YYYY")}
                                                </Typography>
                                            </Grid>
                                            {isPaid && (
                                                <>
                                                    <Grid item xs={5}>
                                                        <Typography variant="body1">Processed by:</Typography>
                                                    </Grid>
                                                    <Grid item xs={7}>
                                                        <Typography variant="body1" sx={{ fontFamily: FontFamily.NunitoRegular }}>
                                                            {billDetails?.process_by || '-'}
                                                        </Typography>
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>


                             <Grid container spacing={2} >
                                                                 <Grid item xs={12} sm={12} sx={{ paddingRight: "30px", paddingLeft: '30px', paddingBottom: '15px' }}>
                                                                     <Grid
                                                                         container
                                                                         sx={{ border: "1px solid #ddd", marginTop: "4px", marginLeft: "15px", fontFamily: "Helvetica" }}
                                                                     >
                                                                         <Grid item xs={12}>
                                                                             <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                                                                 <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                                                                                     <table style={{ width: "100%", borderCollapse: "collapse", margin: 0, padding: 0, tableLayout: "fixed" }}>
                                                                                         {isPaid ? (
                                                                                             <>
                                                                                                 <thead>
                                                                                                     <tr style={{ background: "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)", color: Colors.whiteSmoke, fontWeight: "bold" }}>
                                                                                                         {["Sr.#", "Date", "Bill no.", "Ref no.", "Total Amount (د.إ)", "Balance (د.إ)"].map((h, i) => (
                                                                                                             <th key={i} style={{ padding: "8px", textAlign: "center", borderBottom: "2px solid #ddd", fontSize: "12px" }}>{h}</th>
                                                                                                         ))}
                                                                                                     </tr>
                                                                                                 </thead>
                                                                                                 <tbody>
                                                                                                     <tr>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", borderLeft: "1px solid #ddd", fontSize: "12px" }}>1</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{moment(billDetails?.bill_date).format("DD-MM-YYYY")}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{billDetails?.bill_no ?? '-'}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{billDetails?.bill_reference_no ?? '-'}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{CommaSeparator(totalAmount.toFixed(2))}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{CommaSeparator(balance.toFixed(2))}</td>
                                                                                                     </tr>
                                                                                                 </tbody>
                                                                                                 {/* USD table */}
                                                                                                 <thead>
                                                                                                     <tr style={{ background: "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)", color: Colors.whiteSmoke, fontWeight: "bold" }}>
                                                                                                         {["Sr.#", "Bill Date", "Bill no.", "Ext. Ref. #", "Paid Amt ($)", "Balance ($)"].map((h, i) => (
                                                                                                             <th key={i} style={{ padding: "8px", textAlign: "center", borderBottom: "2px solid #ddd", fontSize: "12px" }}>{h}</th>
                                                                                                         ))}
                                                                                                     </tr>
                                                                                                 </thead>
                                                                                                 <tbody>
                                                                                                     <tr>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", borderLeft: "1px solid #ddd", fontSize: "12px" }}>2</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{moment(billDetails?.bill_date).format("DD-MM-YYYY")}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{billDetails?.bill_no ?? '-'}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{billDetails?.external_ref_no ?? '-'}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{CommaSeparator(convertAedToUsd(paidAmount).toFixed(2))}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{CommaSeparator(convertAedToUsd(balance).toFixed(2))}</td>
                                                                                                     </tr>
                                                                                                     <tr>
                                                                                                         <td colSpan={4} style={{ padding: "10px", textAlign: "center", fontWeight: "bold", fontSize: "13px", borderTop: "1px solid #000", borderRight: "1px solid #ddd" }}>Total Paid</td>
                                                                                                         <td colSpan={2} style={{ padding: "10px", textAlign: "center", fontWeight: "bold", fontSize: "13px", borderTop: "1px solid #000" }}>{"$ " + CommaSeparator(Number(convertAedToUsd(paidAmount) || 0).toFixed(2))}</td>
                                                                                                     </tr>
                                                                                                 </tbody>
                                                                                             </>
                                                                                         ) : (
                                                                                             <>
                                                                                                 <thead>
                                                                                                     <tr style={{ background: "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)", color: Colors.whiteSmoke, fontWeight: "bold" }}>
                                                                                                         {["Sr.#", "Date", "Bill no.", "Ref no.", "Total Amount (د.إ)", "Balance (د.إ)"].map((h, i) => (
                                                                                                             <th key={i} style={{ padding: "8px", textAlign: "center", borderBottom: "2px solid #ddd", fontSize: "12px" }}>{h}</th>
                                                                                                         ))}
                                                                                                     </tr>
                                                                                                 </thead>
                                                                                                 <tbody>
                                                                                                     <tr>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", borderLeft: "1px solid #ddd", fontSize: "12px", width: "10%" }}>{billDetails?.id}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px", width: '15%' }}>{moment(billDetails?.bill_date).format("DD-MM-YYYY")}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px", width: "15%" }}>{billDetails?.bill_no ?? '-'}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px", width: "15%" }}>{billDetails?.bill_reference_no ?? '-'}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px", width: "15%" }}>{CommaSeparator(totalAmount.toFixed(2))}</td>
                                                                                                         <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px", width: "15%" }}>{CommaSeparator(balance.toFixed(2))}</td>
                                                                                                     </tr>
                                                                                                 </tbody>
                                                                                             </>
                                                                                         )}
                                                                                     </table>
                                                                                 </Box>
                                                                             </Box>
                                                                         </Grid>
                                                                     </Grid>
                                                                 </Grid>
                                                             </Grid>

                                {/* COA Accounts Table — shown only for unpaid bills */}
                               {!isPaid && (
                               <Grid container spacing={2}>
                                   <Grid item xs={12} sm={12} sx={{ paddingRight: "30px", paddingLeft: "30px", paddingBottom: "15px" }}>
                                       <Grid container sx={{ border: "1px solid #ddd", marginTop: "4px", marginLeft: "15px", fontFamily: "Helvetica" }}>
                                           <Grid item xs={12}>
                                               <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                                                   <table style={{ width: "100%", borderCollapse: "collapse", margin: 0, padding: 0, tableLayout: "fixed" }}>
                                                       <thead>
                                                           <tr style={{ background: "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)", color: Colors.whiteSmoke, fontWeight: "bold" }}>
                                                               {["Sr.#", "COA Code", "COA Name", "Description", "Amount (د.إ)"].map((h, i) => (
                                                                   <th key={i} style={{ padding: "8px", textAlign: "center", borderBottom: "2px solid #ddd", fontSize: "12px" }}>{h}</th>
                                                               ))}
                                                           </tr>
                                                       </thead>
                                                       <tbody>
                                                           {(billDetails?.bill_accounts ?? []).map((account, idx) => (
                                                               <tr key={idx}>
                                                                   <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", borderLeft: "1px solid #ddd", fontSize: "12px" }}>{idx + 1}</td>
                                                                   <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{account?.account_id ?? "-"}</td>
                                                                   <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>
                                                                       <Tooltip title={account?.full_path ?? ""} arrow>
                                                                           <span>{getLastPathSegment(account?.full_path)}</span>
                                                                       </Tooltip>
                                                                   </td>
                                                                   <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{account?.description ?? "-"}</td>
                                                                   <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px" }}>{CommaSeparator(parseFloat(account?.bill_amount || 0).toFixed(2))}</td>
                                                               </tr>
                                                           ))}
                                                       </tbody>
                                                   </table>
                                               </Box>
                                           </Grid>
                                       </Grid>
                                   </Grid>
                               </Grid>
                               )}

                               <Grid container spacing={2} sx={{ ml: 0.2 }}>
                               {(groupedPaymentHistory.length > 0) && (
                                        <Grid item xs={6} sm={6} sx={{ padding: "18px", marginTop: "5px", pl: 0.2 }}>
                                            <Box sx={{ border: "1px solid #ddd", marginTop: "10px" }}>
                                                <Grid item xs={12}>
                                                    <Typography variant="h6" sx={{ fontFamily: "Helvetica", textAlign: "center", fontWeight: "bold", padding: "8px" }}>
                                                        Payment History
                                                    </Typography>
                                                </Grid>
                                                <Grid md={12} sx={{ paddingLeft: 0 }}>
                                                    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                                        <Box sx={{ overflowX: "auto", maxWidth: "100%", paddingLeft: 0, marginLeft: 0 }}>
                                                            <table style={{ width: "100%", borderCollapse: "collapse", margin: 0, padding: 0, tableLayout: "fixed" }}>
                                                                <thead>
                                                                    <tr style={{ background: Colors.green, color: Colors.whiteSmoke, fontWeight: "bold" }}>
                                                                        {["Paid on", "Mode", "د.إ", "$"].map((h, i) => (
                                                                            <th key={i} style={{ padding: "8px", textAlign: "center", borderBottom: "2px solid #ddd", fontSize: "12px" }}>{h}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {groupedPaymentHistory.map((payment, index) => (
                                                                        <tr key={index}>
                                                                            <td style={{ padding: "8px", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px", textAlign: "center" }}>
                                                                                {formatPaymentHistoryDate(payment)}
                                                                            </td>
                                                                            <td style={{ padding: "8px", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px", textAlign: "center" }}>
                                                                                {payment?.payment_method === "vault" ? "Wallet" : (payment?.payment_method || "-")}
                                                                            </td>
                                                                            <td style={{ padding: "8px", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px", textAlign: "center" }}>
                                                                                {CommaSeparator(parseFloat(payment?.paid_amount || 0).toFixed(2))}
                                                                            </td>
                                                                            <td style={{ padding: "8px", borderBottom: "1px solid #ddd", borderRight: "1px solid #ddd", fontSize: "12px", textAlign: "center" }}>
                                                                                {CommaSeparator(convertAedToUsd(parseFloat(payment?.paid_amount || 0)).toFixed(2))}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Box>
                                        </Grid>
                                    )}

                                    <Grid item xs={5.6} sm={5.6}>
                                        <Box sx={{ border: "1px solid #ddd", marginTop: "12px" }}>
                                            <Grid item xs={12} bgcolor="#F8F8F8">
                                                <Typography sx={{ fontFamily: "Helvetica", fontWeight: 700, padding: "10px", fontSize: "18px", color: "#0C6135" }}>
                                                    Remarks
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} bgcolor="#F8F8F8">
                                                <Typography sx={{ fontFamily: "Helvetica", fontWeight: 700, padding: "10px", fontSize: "14px" }}>
                                                    {receiptRemarks}
                                                </Typography>
                                            </Grid>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1} padding="30px">

                                    <Grid item xs={10}>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            PLEASE READ CAREFULLY THE T&C:
                                        </Typography>
                                        <Box
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: "12px",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                className={classes.text}
                                                sx={{ mb: 1 }}
                                            >
                                                1- Kindly check or review the invoice/voucher and cash
                                                before leaving the counter.
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                className={classes.text}
                                                sx={{ mb: 1 }}
                                            >
                                                2- Our establishment will not be responsible for any
                                                delay, mistakes, or omissions in transmission.
                                            </Typography>{" "}
                                            <Typography
                                                variant="body2"
                                                className={classes.text}
                                                sx={{ mb: 1 }}
                                            >
                                                3- Our liability is limited to making all necessary
                                                amendments in the shortest possible time.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={2} sm={2} md={2}>
                                        <Box
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <QRCodeCanvas
                                                value={
                                                    window.location.origin +
                                                    `/supplier-bill-preview/${btoa(id)}`
                                                }
                                            />
                                        </Box>
                                        {/* <Box
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                                {(() => {
                                                    const baseUrl = (
                                                        process.env.REACT_APP_QR_BASE_URL ||
                                                        process.env.REACT_APP_BASE_URL_FRONTEND ||
                                                        window.location.origin
                                                    )
                                                        ?.toString()
                                                        .trim()
                                                        .replace(/\/$/, "");

                                                    const url = `${baseUrl}/supplier-bill-preview/${btoa(
                                                        String(id)
                                                    )}`;

                                                    return (
                                                        <Box
                                                            component="a"
                                                            href={url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            title="Open preview on this PC"
                                                            sx={{ display: "inline-flex" }}
                                                        >
                                                            <QRCodeCanvas value={url} />
                                                        </Box>
                                                    );
                                                })()}
                                        </Box> */}
                                    </Grid>
                                </Grid>

                                <Box sx={{ pl: 4, pr: 3, py: 1, backgroundColor: "#0C6135" }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: Colors.white,
                                            fontFamily: FontFamily.openSans,
                                        }}
                                    >
                                        Customer care Contact: Mohammed husni - +971523195682 (Arabic
                                        & English ) Ardamehr Shoev - +971545836028 (English ,Arabic,
                                        Tajik & Farsi)
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: Colors.white,
                                            fontFamily: FontFamily.openSans,
                                        }}
                                    >
                                        Ravin abdul kareem - +971528293801 (Kurdish , Arabic &
                                        English) Magsat Gylyjov - +97158666403 (Turken , Russian &
                                        English)
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: "center", py: 3 }}>
                                <CircularProgress />
                            </Box>
                        )}

                    </>
                </Box>
            </PDFExport>
        </Container>
    );
}

export default SupplierBillApprovalReceipt;
